import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BRAND_NAME as DEFAULT_NAME, BRAND_TAGLINE as DEFAULT_TAGLINE, BRAND_LOGO as DEFAULT_LOGO } from "@/lib/brand";

interface BrandContextType {
  name: string;
  tagline: string;
  logoUrl: string;
  loading: boolean;
  refresh: () => Promise<void>;
}

const BrandContext = createContext<BrandContextType>({
  name: DEFAULT_NAME,
  tagline: DEFAULT_TAGLINE,
  logoUrl: DEFAULT_LOGO,
  loading: true,
  refresh: async () => {},
});

export const BrandProvider = ({ children }: { children: ReactNode }) => {
  const [name, setName] = useState(DEFAULT_NAME);
  const [tagline, setTagline] = useState(DEFAULT_TAGLINE);
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_LOGO);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const { data } = await supabase.from("company_settings").select("name, tagline, logo_url").eq("id", true).maybeSingle();
    if (data) {
      setName(data.name || DEFAULT_NAME);
      setTagline(data.tagline || DEFAULT_TAGLINE);
      setLogoUrl(data.logo_url || DEFAULT_LOGO);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <BrandContext.Provider value={{ name, tagline, logoUrl, loading, refresh }}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
